import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  createQueryParams,
  getQueryUrlFromData,
} from '@/common/utils/transformFilters'
import { HYDRATE } from 'next-redux-wrapper'
import { open } from '@/store/modalsSlice'
import { LIMIT_MODAL } from '@/common/constants/modals'
import { LIMIT_CODE } from '@/common/constants/responseCodes'
import { searchInitialState } from '@/store/states/SearchInitialState'
import {
  resetAllFilters,
  resetFilterToDefaultByInputs,
  resetToDefaultFilter,
  setCurrentLayoutType,
  setFiltersParams,
  setIsInfiniteScroll,
  setIsLoadingItems,
  setItems,
  setPagesMap,
  setSelectedItem,
  updateFiltersParams,
  updatePaginationParams,
} from '@/store/reducers/SearchReducers'
import {
  fetchFiltersConfigPending,
  fetchFiltersConfigReject,
  fetchFiltersConfigSuccess,
  fetchItemsPending,
  fetchItemsRejected,
  fetchItemsSuccess,
  searchHydrate,
} from '@/store/actions/searchActions'
import { fetchFiltersConfig } from '@/store/thunks/searchThunks'
import { SEARCH_ADS } from '@/common/constants/search'
import { APPLIED_CREATIVES_FILTERS } from '@/common/constants/filtersFields'
import { setCookie } from 'cookies-next'

export const fetchCreatives = createAsyncThunk(
  'creatives/fetchCreatives',
  async (
    {
      withInfiniteScroll = false,
      request,
      params = {},
      itemsName = 'advertisements',
      isUpdateCookieFilters,
      signal,
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      if (!withInfiniteScroll) {
        const { setCreatives } = creativesSlice.actions
        dispatch(setCreatives([]))
      }

      const filters = getState().creatives.filtersParams

      const queryMap = {
        ...params,
        ...createQueryParams(filters),
      }
      const query = getQueryUrlFromData(queryMap)

      if (isUpdateCookieFilters) {
        setCookie(APPLIED_CREATIVES_FILTERS, query)
      }

      const { creativeIdentical } = query
      const { [itemsName]: items, pagination } = await request(queryMap, signal)

      const modifiedCreatives = items.map((item) => ({
        ...item,
        isTranslate: false,
      }))

      return {
        items: modifiedCreatives,
        pagination: pagination || { pages: {}, results: {} },
        withInfiniteScroll: withInfiniteScroll,
        isVisibleFilters: !creativeIdentical,
      }
    } catch (e) {
      const headers = e?.response?.headers ?? {}
      const error = e.data?.error ?? {}
      const response = e.response ?? {}

      if (error?.code === LIMIT_CODE) {
        await dispatch(open(LIMIT_MODAL))
      }

      return rejectWithValue({ ...headers, ...error, ...response })
    }
  }
)

export const fetchCreative = createAsyncThunk(
  'creatives/fetchCreative',
  async ({ params, request }, { rejectWithValue }) => {
    try {
      const data = await request(params)
      return data
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const creativesSlice = createSlice({
  name: 'creatives',
  initialState: {
    ...searchInitialState,
    isCreativesFiltersOpen: true,
    isCreativeDetailsLoading: false,
    isVisibleBlackListModal: false,
    currentSlideData: null,
    needFetchCreatives: true,
    favoriteGroupsMap: {
      pagination: null,
      groups: [],
    },
    selectedActiveChip: null,
    activeVideo: null,
    prevActiveVideo: null,
    isCoverage: false,
    isActiveBlackList: false,
  },
  reducers: {
    setIsActiveBlackList(state, { payload }) {
      state.isActiveBlackList = payload
    },
    openCreativesFilters(state) {
      state.isCreativesFiltersOpen = true
    },
    closeCreativesFilters(state) {
      state.isCreativesFiltersOpen = false
    },
    setLayoutType: setCurrentLayoutType,
    setFavoriteGroupsMap(state, { payload }) {
      state.favoriteGroupsMap = payload
    },
    setIsVisibleBlackListModal(state, { payload }) {
      state.isVisibleBlackListModal = payload
    },
    setIsLoadingCreatives: setIsLoadingItems,
    setIsActiveInfiniteScroll: setIsInfiniteScroll,
    setIsFetchCreatives(state, { payload }) {
      state.needFetchCreatives = payload
    },
    setCurrentSlideData(state, { payload }) {
      state.currentSlideData = payload
    },
    updatePaginationParamsCreatives: updatePaginationParams,
    setCreatives: setItems,
    setPagesMapCreatives: setPagesMap,
    setSelectedCreative: setSelectedItem,
    setIsCoverage(state, { payload }) {
      state.isCoverage = payload
    },
    setSelectedActiveChip(state, { payload }) {
      state.selectedActiveChip = payload
    },
    setActiveVideo(state, { payload }) {
      state.prevActiveVideo = state.activeVideo
      state.activeVideo = payload
    },
    setIsLoadingByPagination(state, { payload }) {
      state.isLoadingByPagination = payload
    },
    updateFiltersParamsCreatives: updateFiltersParams,
    setFiltersParamsCreatives: setFiltersParams,
    resetToDefaultFilterCreatives: resetToDefaultFilter,
    resetFilterToDefaultByInputsCreatives: resetFilterToDefaultByInputs,
    resetAllFiltersCreatives: resetAllFilters,
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      searchHydrate({ state, action, slice: 'creatives' })
    },
    [fetchCreatives.pending]: fetchItemsPending,
    [fetchCreatives.fulfilled]: fetchItemsSuccess,
    [fetchCreatives.rejected]: fetchItemsRejected,
    [fetchCreative.pending]: (state) => {
      state.isCreativeDetailsLoading = true
    },
    [fetchCreative.fulfilled]: (state, { payload }) => {
      state.isCreativeDetailsLoading = false
      state.selectedItem = payload
    },
    [fetchCreative.rejected]: (state) => {
      state.isCreativeDetailsLoading = false
    },
    [fetchFiltersConfig.pending]: (state, action) => {
      if (action.payload === SEARCH_ADS) {
        fetchFiltersConfigPending(state, action)
      }
    },
    [fetchFiltersConfig.fulfilled]: (state, action) => {
      if (action.payload.sliceName === SEARCH_ADS) {
        fetchFiltersConfigSuccess(state, action)
      }
    },
    [fetchFiltersConfig.rejected]: (state, action) => {
      if (action.payload === SEARCH_ADS) {
        fetchFiltersConfigReject(state, action)
      }
    },
  },
})

export const creativesActions = creativesSlice.actions

export default creativesSlice.reducer
